import { call, takeLatest, put } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import { sendAction } from '../api';
import { sendActionWithRetries, transformUser } from '../shared';
import { addUsers, clearUsers } from '../actions';

// Minimal shape of the config object threaded through the untyped api/shared helpers.
interface SuperClientConfig {
  getAuthToken: () => string;
  baseApiUrl: string;
  timeout?: number;
}

// Wrapped server response shapes (see api/index.js `wrapHttpRequest`).
interface UsersCountResponse {
  succeeded?: boolean;
  data: { totalUsers: number };
}

interface UsersVerboseResponse {
  succeeded?: boolean;
  // Values are keyed users; derive the accepted shape from transformUser's own input type.
  data: Record<string, Parameters<typeof transformUser>[0]>;
}

const maxAttempts = 2;
const attemptCount = 1;
const reattemptDelay = 11000;
const requestSpacing = 4000;

// Fetch license data on load or after change
export function* fetchAllUsers(config: SuperClientConfig): Generator<unknown, void, unknown> {
  try {
    const maxUsers = 10000;

    const userCountRes = (yield call(
      sendAction,
      config,
      CloudActions.getUsersCount(),
    )) as UsersCountResponse | undefined;
    if (!userCountRes || !userCountRes.succeeded) throw new Error('getUsersCount failed');

    const userCount = userCountRes.data.totalUsers;

    if (userCount > 0) {
      const requestsCount = Math.ceil(userCount / maxUsers);

      yield put(clearUsers());

      for (let i = 0; i < requestsCount; i += 1) {
        const offset = i * maxUsers;
        const action = CloudActions.getUsersVerbosePaginate(maxUsers, offset);
        const res = (yield call(
          sendActionWithRetries,
          config,
          action,
          maxAttempts,
          attemptCount,
          reattemptDelay,
          requestSpacing,
        )) as UsersVerboseResponse;

        if (res.succeeded) {
          const usersArray = Object.values(res.data).map(transformUser);
          yield put(addUsers(usersArray));
        } else {
          throw new Error('fetchAllUsers response failed');
        }
      }
    }
  } catch (error) {
    console.error('fetchAllUsers failed -- ', error);
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchAllUsers(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(CloudActions.ADMIN_GET_USERS_VERBOSE, fetchAllUsers, config);
}
